import React, { useRef, useState, useEffect } from 'react';
import styled from 'styled-components';
import { typography, CardButtonBase } from '../../../styled';
import { CardSelectionHighlightBase, DiscountBase } from './styled';
import { centsToCurrency, cardTitle } from './util';
import { CycleCardProps } from '../types';
import { themes } from 'kb-shared';
const { colors, breakpoints } = themes;
const {
  serif: {
    card: { H3 }
  },
  sans: {
    copy: { Small }
  },
  spans: { Bold }
} = typography;

const Card = styled.div`
  flex-grow: 1;
  margin-right: 8px;
  max-width: 33%;

  &:last-of-type {
    margin-right: 0;
  }

  @media (max-width: ${breakpoints.largePhonePortrait}) {
    max-width: 100%;
    margin-bottom: 12px;

    &:last-of-type {
      margin-bottom: 24px;
    }
  }
`;

const CardButton = styled(CardButtonBase)`
  align-items: center;
  flex-direction: column;
  justify-content: flex-start;
  padding: 25px;
`;

interface CardSelectionHighlightTopProps {
  selected?: boolean;
}

const CardSelectionHighlightTop = styled(CardSelectionHighlightBase)`
  height: ${({ selected }: CardSelectionHighlightTopProps) => (selected ? '10px' : '0px')};
  position: absolute;
  transition: all 0.2s;
  top: 0;
  width: 100%;
`;

const Discount = styled(DiscountBase)`
  margin-top: 20px;
  ${({ hasDiscount, isMember }: { hasDiscount?: boolean; isMember?: boolean }) => {
    if (!hasDiscount) return 'opacity: 0;';
    if (isMember) return `background-color: ${colors.yellow}`;
  }};
`;

const MemberPricing = styled(DiscountBase)`
  margin-top: 10px;
`;

const PinkDiscount = styled(Discount)`
  background-color: ${({ theme }) => theme.colors.pink};
  margin-bottom: 5px;
`;

const Detail = styled.p`
  ${({ theme }) => theme.fonts.FoundersGrotesk};
`;

const Title = styled(H3)``;

const CycleCard = (props: CycleCardProps) => {
  const { cycle, onSelect, selected, selectedCycleType, depositPromoCode } = props;
  const { promo, promoType } = cycle;
  const { name, rateCents, discountRateCents } = cycle;
  const btn = useRef(null);

  const [price, setPrice] = useState(rateCents);
  const [discount, setDiscount] = useState(discountRateCents);

  useEffect(() => {
    if (!promo) return;
    const { totalCents, discountCents } = promo;
    if (['IVF', 'Egg Freezing', 'Embryo Banking'].indexOf(selectedCycleType) > -1) {
      setPrice(totalCents);
      setDiscount(discountCents);
    }
  }, [cycle, promo, selectedCycleType]);

  const hasMemberPromo = promoType === 'membership';
  const hasDiscount = discount !== 0;
  const depositPromoPrice = rateCents - 100000;
  const depositPromoIUIPrice = rateCents - 20000;
  const depositPromoApplies =
    depositPromoCode &&
    ['IVF', 'Egg Freezing', 'Embryo Banking'].indexOf(selectedCycleType) > -1 &&
    (depositPromoPrice < price ||
      // only show if deposit promo makes it cheaper than membership discount, if they have one
      (promo && promo.code.toUpperCase() === depositPromoCode && depositPromoPrice < rateCents)); // this ensures deposit promo still displays in card AFTER promo has been applied
  const depositPromotIUIApplies =
    depositPromoCode &&
    ['IUI'].indexOf(selectedCycleType) > -1 &&
    (depositPromoIUIPrice < price ||
      // only show if deposit promo makes it cheaper than membership discount, if they have one
      (promo && promo.code.toUpperCase() === depositPromoCode && depositPromoPrice < rateCents)); // this ensures deposit promo still displays in card AFTER promo has been applied
  return (
    <Card>
      <CardButton
        ref={btn}
        onClick={() => {
          const { current } = btn;
          // @ts-ignore
          if (current && current.blur) current.blur();
          onSelect(cycle);
        }}
      >
        <CardSelectionHighlightTop selected={selected} />
        <Title>{cardTitle(name, selectedCycleType)}</Title>
        <Small>
          <Bold>{`$${centsToCurrency(
            depositPromoApplies || depositPromotIUIApplies ? rateCents : price
          )}`}</Bold>
        </Small>
        {depositPromoApplies || depositPromotIUIApplies ? (
          <>
            <PinkDiscount hasDiscount={true}>
              <strong>
                {depositPromoApplies
                  ? `$${centsToCurrency(depositPromoPrice)}`
                  : `$${centsToCurrency(depositPromoIUIPrice)}`}{' '}
                with $500 deposit
              </strong>
            </PinkDiscount>
            <Detail>Code: {depositPromoCode}</Detail>
          </>
        ) : (
          <Discount hasDiscount={hasDiscount} isMember={hasMemberPromo}>{`Save $${centsToCurrency(
            discount
          )}`}</Discount>
        )}
        {hasMemberPromo && hasDiscount && !depositPromoApplies ? (
          <MemberPricing>MEMBER PRICING</MemberPricing>
        ) : null}
      </CardButton>
    </Card>
  );
};

export default CycleCard;
